// Products.jsx - Quản lý sản phẩm (Tiếng Việt) - Đã cập nhật responsive
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme,
  Stack,
  InputAdornment,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Visibility,
  Edit,
  Delete,
  Search,
  FileDownload,
} from "@mui/icons-material";
import useLayoutStore from "../../store/useLayoutStore";
import AddProductModal from "./AddProductModal";
import UpdateProductModal from "./UpdateProductModal";
import ImportProductModal from "./ImportProductModal";
import ViewProductModal from "./ViewProductModal";
import CustomTable from "../../components/common/CustomTable";
import CustomPaging from "../../components/common/CustomPaging";
import CustomButton from "../../components/common/CustomButton";
import axios from "axios";
import config from "../../config";

const Products = () => {
  const theme = useTheme();
  const { isSidebarOpen } = useLayoutStore();
  
  // ✅ Breakpoints (giống Orders component)
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.between("lg", "xl"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("xl"));

  // ✅ Chiều rộng sidebar theo từng màn hình (giống Orders component)
  let drawerW = 0;
  if (isMobile) {
    drawerW = 0;
  } else if (isTablet) {
    drawerW = 200;
  } else if (isLaptop) {
    drawerW = isSidebarOpen ? 240 : 80;
  } else if (isDesktop) {
    drawerW = isSidebarOpen ? 280 : 100;
  } else if (isLargeScreen) {
    drawerW = isSidebarOpen ? 320 : 120;
  }

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(isMobile ? 10 : 15);
  const [page, setPage] = useState(1);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openImportModal, setOpenImportModal] = useState(false);
  const [openSizeModal, setOpenSizeModal] = useState(false);
  const [selectedSizeProduct, setSelectedSizeProduct] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/Products`);
        const data = response.data;
        setProducts(
          data.map((item) => ({
            id: item.productId,
            name: item.name,
            sku: item.sku,
            category: item.category,
            price: item.price,
            quantity: item.totalStock,
            sales: item.saleCount,
            stockStatus: item.totalStock > 0 ? "Còn hàng" : "Hết hàng",
            rating: item.rating,
            img: item.imageUrls?.length
              ? item.imageUrls[0]
              : "https://via.placeholder.com/50",
            variations: item.variations || [],
          }))
        );
        
        // Extract unique categories
        const uniqueCategories = [...new Set(data.map(item => item.category))];
        setCategories(uniqueCategories);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleViewSizes = (productId) => {
    const product = products.find((p) => p.id === productId);
    setSelectedSizeProduct(product);
    setOpenSizeModal(true);
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.sku.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || product.category === categoryFilter;
    const matchesStock = !stockFilter || 
                        (stockFilter === "inStock" && product.quantity > 0) ||
                        (stockFilter === "outOfStock" && product.quantity === 0);
    
    return matchesSearch && matchesCategory && matchesStock;
  });

  const startIndex = (page - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleView = (product) => {
    setSelectedProduct(product);
    setOpenViewModal(true);
  };

  const handleEdit = (product) => {
    setEditingProductId(product.id);
    setOpenEditModal(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa sản phẩm này?")) {
      try {
        await axios.delete(`${config.apiBaseUrl}/Products/${productId}`);
        setProducts(products.filter((product) => product.id !== productId));
      } catch (error) {
        console.error("Lỗi khi xóa sản phẩm:", error);
        alert("Không thể xóa sản phẩm. Vui lòng thử lại.");
      }
    }
  };

  const exportToExcel = () => {
    const exportData = products.map((p) => ({
      "Tên sản phẩm": p.name,
      "SKU": p.sku,
      "Danh mục": p.category,
      "Giá bán": p.price,
      "Tồn kho": p.quantity,
      "Đã bán": p.sales,
      "Trạng thái": p.stockStatus,
    }));
    
    // const worksheet = XLSX.utils.json_to_sheet(exportData);
    // const workbook = XLSX.utils.book_new();
    // XLSX.utils.book_append_sheet(workbook, worksheet, "Sản phẩm");
    // XLSX.writeFile(workbook, "DanhSachSanPham.xlsx");
  };

  const productColumns = [
    {
      field: "img",
      label: "Hình ảnh",
      minWidth: isMobile ? "60px" : "80px",
      customRender: (row) => (
        <Avatar
          src={row.img}
          sx={{ width: isMobile ? 180 : 190, height: isMobile ? 180 : 190 }}
          variant="rounded"
        />
      ),
      hideOnMobile: false
    },
    { 
      field: "name", 
      label: "Tên sản phẩm", 
      minWidth: isMobile ? "150px" : "200px",
      hideOnMobile: false
    },
    { 
      field: "sku", 
      label: "SKU", 
      minWidth: isMobile ? "100px" : "150px",
      hideOnMobile: true
    },
    { 
      field: "price", 
      label: "Giá bán", 
      minWidth: isMobile ? "80px" : "100px",
      customRender: (row) => `${row.price.toLocaleString()}₫`,
      hideOnMobile: false
    },
    { 
      field: "quantity", 
      label: "Tồn kho", 
      minWidth: isMobile ? "70px" : "100px",
      hideOnMobile: true
    },
    {
      field: "sizes",
      label: "Size",
      minWidth: isMobile ? "80px" : "120px",
      customRender: (row) => {
        const fullProduct = products.find((p) => p.id === row.id);
        if (!fullProduct || !fullProduct.variations) return "N/A";

        const sizeCount = fullProduct.variations.reduce((total, variation) => {
          return total + (variation.sizes ? variation.sizes.length : 0);
        }, 0);

        return (
          <Tooltip title="Xem chi tiết size">
            <Chip
              label={`${sizeCount} size`}
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => handleViewSizes(row.id)}
              sx={{ fontSize: isMobile ? "0.7rem" : "0.8rem" }}
            />
          </Tooltip>
        );
      },
      hideOnMobile: true
    },
    {
      field: "actions",
      label: "Thao tác",
      minWidth: isMobile ? "120px" : "150px",
      customRender: (row) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
          <Tooltip title="Xem chi tiết">
            <IconButton 
              color="primary" 
              onClick={() => handleView(row)}
              size="small"
            >
              <Visibility fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Chỉnh sửa">
            <IconButton 
              color="secondary" 
              onClick={() => handleEdit(row)}
              size="small"
            >
              <Edit fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Xóa">
            <IconButton 
              color="error" 
              onClick={() => handleDelete(row.id)}
              size="small"
            >
              <Delete fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      hideOnMobile: false
    },
  ];

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 1, sm: 2 },
        width: isMobile || isTablet ? "100vw" : `calc(100vw - ${drawerW}px)`,
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
            Danh sách sản phẩm
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Đang hiển thị {(page - 1) * itemsPerPage + 1}–
            {Math.min(page * itemsPerPage, filteredProducts.length)} trong tổng số{" "}
            {filteredProducts.length} sản phẩm
          </Typography>
        </Box>
        
        <Box sx={{ 
          display: "flex", 
          gap: 1, 
          flexDirection: { xs: "column", sm: "row" },
          width: { xs: "100%", sm: "auto" }
        }}>
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={exportToExcel}
            size="small"
            sx={{ 
              minWidth: { xs: "100%", sm: "auto" },
              width: { xs: "100%", sm: "auto" }
            }}
          >
            {isMobile ? "Excel" : "Xuất Excel"}
          </Button>
          <CustomButton
            text={isMobile ? "+ Thêm" : "+ Thêm sản phẩm"}
            onClick={() => setOpenAddModal(true)}
            customColor="#1976d2"
            size="small"
            sx={{ 
              minWidth: { xs: "100%", sm: "auto" },
              width: { xs: "100%", sm: "auto" }
            }}
          />
          <CustomButton
            text={isMobile ? "📅 Excel" : "📅 Nhập Excel"}
            onClick={() => setOpenImportModal(true)}
            customColor="#4caf50"
            size="small"
            sx={{ 
              minWidth: { xs: "100%", sm: "auto" },
              width: { xs: "100%", sm: "auto" }
            }}
          />
        </Box>
      </Box>

      {/* Search + Filter */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box sx={{ 
          display: "flex", 
          gap: 1, 
          flexDirection: { xs: "column", sm: "row" },
          width: { xs: "100%", sm: "auto" }
        }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Tìm kiếm sản phẩm..."
            size="small"
            sx={{ 
              minWidth: { xs: "100%", sm: 250, md: 300 },
              width: { xs: "100%", sm: "auto" }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl 
            sx={{ 
              minWidth: { xs: "100%", sm: 150, md: 180 },
              width: { xs: "100%", sm: "auto" }
            }}
            size="small"
          >
            <InputLabel>Danh mục</InputLabel>
            <Select
              value={categoryFilter}
              label="Danh mục"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">Tất cả</MenuItem>
              {categories.map((category, index) => (
                <MenuItem key={index} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl 
            sx={{ 
              minWidth: { xs: "100%", sm: 140, md: 160 },
              width: { xs: "100%", sm: "auto" }
            }}
            size="small"
          >
            <InputLabel>Tồn kho</InputLabel>
            <Select
              value={stockFilter}
              label="Tồn kho"
              onChange={(e) => setStockFilter(e.target.value)}
            >
              <MenuItem value="">Tất cả</MenuItem>
              <MenuItem value="inStock">Còn hàng</MenuItem>
              <MenuItem value="outOfStock">Hết hàng</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Stack>

      {/* Table / Loading / Error */}
      {loading ? (
        <CircularProgress sx={{ alignSelf: "center", mt: 5 }} />
      ) : error ? (
        <Typography color="error" sx={{ alignSelf: "center", mt: 5 }}>
          {error}
        </Typography>
      ) : (
        <>
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <CustomTable 
              columns={productColumns} 
              data={paginatedProducts} 
              isMobile={isMobile}
            />
          </Box>

          {/* Paging */}
          <CustomPaging
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            totalItems={filteredProducts.length}
            isMobile={isMobile}
          />
        </>
      )}

      {/* Empty state */}
      {!loading && products.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <Typography variant="body1" align="center">
            Chưa có sản phẩm nào
          </Typography>
        </Box>
      )}

      {/* No results state */}
      {!loading && products.length > 0 && filteredProducts.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
          <Typography variant="body1" align="center">
            Không tìm thấy sản phẩm phù hợp
          </Typography>
        </Box>
      )}

      {/* Modals */}
      <AddProductModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onProductAdded={(newProduct) => {
          setProducts([...products, newProduct]);
        }}
      />

      <ImportProductModal
        open={openImportModal}
        onClose={() => setOpenImportModal(false)}
        onImportSuccess={() => window.location.reload()}
      />

      <Dialog
        open={openSizeModal}
        onClose={() => setOpenSizeModal(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Thông tin size - {selectedSizeProduct?.name}
        </DialogTitle>
        <DialogContent>
          {selectedSizeProduct && selectedSizeProduct.variations && (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Màu sắc</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell align="right">Số lượng</TableCell>
                    <TableCell align="right">Giá</TableCell>
                    <TableCell>Trạng thái</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSizeProduct.variations.map((variation, index) =>
                    variation.sizes.map((size, idx) => (
                      <TableRow key={`${index}-${idx}`}>
                        <TableCell>{variation.color}</TableCell>
                        <TableCell>{size.size}</TableCell>
                        <TableCell align="right">{size.stock}</TableCell>
                        <TableCell align="right">
                          {size.price?.toLocaleString()}₫
                        </TableCell>
                        <TableCell>{size.status}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSizeModal(false)}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {selectedProduct && (
        <ViewProductModal
          open={openViewModal}
          onClose={() => setOpenViewModal(false)}
          product={selectedProduct}
          fullScreen={isMobile}
        />
      )}

      {openEditModal && editingProductId && (
        <UpdateProductModal
          open={openEditModal}
          onClose={() => {
            setOpenEditModal(false);
            setEditingProductId(null);
          }}
          onSave={(updatedProduct) => {
            setProducts((prevProducts) =>
              prevProducts.map((p) =>
                p.id === updatedProduct.id ? updatedProduct : p
              )
            );
          }}
          productId={editingProductId}
          fullScreen={isMobile}
        />
      )}
    </Box>
  );
};

export default Products;